// The AI-Era FAIR engine is just ONE engine that feeds the standard report
// view. This maps its rich `ReportData` into the engine-agnostic `ReportModel`
// data bag the section library reads, and provides the default composition
// (`AI_ERA_REPORT_CONFIG`) an admin can start from and then reshape per
// assessment (persisted to `assessment.metadata.reportLayout`). Pure — no React.

import type { ReportData, RecommendationDetail } from './report/types';
import type { ReportConfig, ReportModel } from '../reportView/types';

function mapRecs(recs: RecommendationDetail[]) {
  return recs.map((r) => ({
    controlId: r.controlId,
    rank: r.rank,
    title: r.controlName,
    subtitle: `${r.domainLabel} · ${r.currentLabel} → ${r.targetLabel}`,
    scoreDrop: r.expectedScoreDrop,
    bodyByRole: {
      portfolioManager: r.roleActions.portfolioManager,
      securityEngineer: r.roleActions.securityEngineer,
    },
    steps: r.actionSteps,
    tags: r.threatsAddressed.map((t) => t.name),
  }));
}

/** Flatten the FAIR report into the generic, keyed data bag. */
export function aiEraReportToModel(report: ReportData): ReportModel {
  const exec = report.executiveSummary;

  const gaps = exec.biggestGaps.map(
    (g) => `${g.domainLabel}: ${g.currentLabel} → ${g.targetLabel}`
  );

  return {
    meta: {
      orgName: report.branding.orgName,
      generatedAt: report.generatedAt,
      consolidationMode: report.consolidationMode,
    },
    data: {
      overall: {
        overline: `${report.branding.orgName || 'AI-Era Cyber Defense'} · Consolidated risk`,
        score: report.overall.score,
        scoreMax: 100,
        label: report.overall.label,
        kpis: [
          { label: 'Maturity stage', value: `Stage ${exec.maturityStage.stage}`, hint: exec.maturityStage.label },
          { label: 'Critical threats', value: String(report.overall.criticalCount) },
          { label: 'High threats', value: String(report.overall.highCount) },
          { label: 'Technology domains', value: String(report.overall.useCaseCount) },
        ],
      },
      executiveSummary: {
        text: exec.narrative,
        groups: [
          ...(gaps.length ? [{ label: 'Widest maturity gaps', items: gaps }] : []),
          ...(exec.nextSteps.length ? [{ label: 'Next steps', items: exec.nextSteps }] : []),
        ],
      },
      scope: { text: report.scopeNote },
      topThreats: {
        items: report.topThreats.map((t) => ({
          name: t.name,
          score: t.exposureScore,
          severity: t.severity,
          meta: `L ${t.likelihoodPct}% × I ${t.impactPct}%`,
        })),
      },
      focusedPlan: { items: mapRecs(report.focusedPlan) },
      recommendations: { items: mapRecs(report.recommendations) },
      domainMaturity: {
        axes: report.domainMaturity.map((d) => ({
          axis: d.domainId,
          label: d.domainLabel,
          current: Number(d.current.toFixed(2)),
          target: d.target,
        })),
        max: 4,
      },
      threatMatrix: {
        columns: report.threatMatrix.columns,
        rows: report.threatMatrix.rows.map((r) => ({
          label: r.name,
          shortLabel: r.shortLabel,
          cells: r.cells,
        })),
      },
      productExposure: {
        columns: [
          { key: 'productName', label: 'Product' },
          { key: 'controls', label: 'Controls', align: 'right' as const },
          { key: 'threats', label: 'Threats', align: 'right' as const },
          { key: 'reduction', label: 'Risk reduction', align: 'right' as const },
        ],
        rows: report.productExposure.map((p) => ({
          productName: p.productName,
          controls: p.controlsAddressed.length,
          threats: p.threatsAddressed.length,
          reduction: Math.round(p.totalExpectedScoreDrop),
        })),
      },
      architecture: {
        model: report.architectureModel,
        summary: report.architecture
          ? {
              nodeCount: report.architecture.nodeCount,
              linkCount: report.architecture.linkCount,
              legacyCount: report.architecture.legacyCount,
              edited: report.architecture.edited,
            }
          : undefined,
      },
      entityInventory: {
        groups: report.selectedEntities.map((c) => ({
          label: c.categoryLabel,
          items: c.items.map((it) => ({ label: it.label, warn: it.eolRisk })),
        })),
      },
      perUseCase: {
        items: report.perUseCaseAppendix.map((uc) => ({
          title: uc.useCaseName,
          badge: `${uc.overallScore} · ${uc.overallLabel}`,
          badgeColorBy: 'severity' as const,
          subtitle: uc.customDescription,
          lines: uc.topThreats.slice(0, 3).map((t) => `${t.name} — ${t.exposureScore} (${t.severity})`),
        })),
      },
      threatDefinitions: {
        items: report.threatDefinitions.map((t) => ({
          title: t.name,
          subtitle: t.category,
          body: t.description,
        })),
      },
      controlDefinitions: {
        items: report.controlDefinitions.map((c) => ({
          title: c.name,
          subtitle: c.domainName,
          body: c.description,
        })),
      },
      methodology: {
        text:
          `Risk is modelled per FAIR as likelihood × impact for each in-scope threat and domain, ` +
          `reduced by the maturity of the controls reported, then consolidated across ` +
          `${report.overall.useCaseCount} technology domain${report.overall.useCaseCount === 1 ? '' : 's'} ` +
          `using the ${report.consolidationMode === 'max' ? 'worst-case (max)' : 'weighted-average'} rule. ` +
          `The raw 0–1 exposure is rescaled to 0–100 against the same band thresholds the severity labels use. ` +
          `Unanswered controls are scored as not in place, so this is a conservative reading.`,
      },
    },
  };
}

/**
 * The default AI-Era report composition. An admin edits a copy of this in the
 * report builder; when `assessment.metadata.reportLayout` is present it wins, so this
 * is only the starting point / fallback.
 */
export const AI_ERA_REPORT_CONFIG: ReportConfig = {
  detailLevels: true,
  defaultDetailLevel: 'business',
  sections: [
    { id: 'overall', type: 'scoreHeader', dataKey: 'overall' },
    { id: 'exec', type: 'narrative', title: 'Executive summary', dataKey: 'executiveSummary' },
    { id: 'scope', type: 'narrative', title: 'Scope', dataKey: 'scope' },
    {
      id: 'top-threats',
      type: 'threatBars',
      title: 'Top threat exposure',
      hint: 'Ranked by calibrated risk score (0–100). Likelihood × impact, reduced by the controls reported.',
      dataKey: 'topThreats',
    },
    {
      id: 'focused-plan',
      type: 'recommendations',
      title: 'Focused remediation plan',
      hint: 'The controls flagged to act on first.',
      dataKey: 'focusedPlan',
      options: { hideWhenEmpty: true },
    },
    {
      id: 'recommendations',
      type: 'recommendations',
      title: 'Recommendations',
      hint: 'Ranked by how much risk each removes, not by effort.',
      dataKey: 'recommendations',
      options: { limitExecutive: 3, stepsMinLevel: 'technical' },
    },
    {
      id: 'domain-maturity',
      type: 'radar',
      title: 'Domain maturity',
      hint: 'Solid shape is where you are; the dashed ring is the L4 target the model assesses against.',
      dataKey: 'domainMaturity',
    },
    {
      id: 'heatmap',
      type: 'heatmap',
      title: 'Where the exposure sits',
      hint: 'Every ranked threat against every domain. Darker cells carry more exposure.',
      dataKey: 'threatMatrix',
    },
    {
      id: 'product-exposure',
      type: 'table',
      title: 'Cisco product coverage',
      hint: 'Every recommended product, ranked by how much risk it addresses.',
      dataKey: 'productExposure',
    },
    { id: 'architecture', type: 'architecture', title: 'Architecture', dataKey: 'architecture' },
    {
      id: 'inventory',
      type: 'chipGroups',
      title: 'Captured inventory',
      hint: 'Grouped by category. End-of-life items are flagged.',
      dataKey: 'entityInventory',
    },
    { id: 'per-use-case', type: 'cardGrid', title: 'Per technology domain', dataKey: 'perUseCase' },
    { id: 'threat-defs', type: 'accordionList', title: 'Threat definitions', dataKey: 'threatDefinitions' },
    { id: 'control-defs', type: 'accordionList', title: 'Control definitions', dataKey: 'controlDefinitions' },
    { id: 'methodology', type: 'narrative', title: 'Methodology', dataKey: 'methodology' },
  ],
};
